import { useState, useCallback } from 'react';
import { cloudClient } from '@/integrations/supabase/cloudClient';
import { useToast } from '@/hooks/use-toast';
import { getStripeEnvironment } from '@/lib/stripeEnv';

/**
 * Opens the Stripe Customer Portal in a new tab so users can manage,
 * pause, or cancel their subscription, update payment methods, and
 * view invoices.
 */
export function usePortalSession() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);

  const open = useCallback(
    async (returnUrl?: string) => {
      setLoading(true);
      try {
        let env: 'sandbox' | 'live';
        try {
          env = getStripeEnvironment();
        } catch {
          throw new Error('Billing is not configured.');
        }
        const { data, error } = await cloudClient.functions.invoke('create-portal-session', {
          body: {
            returnUrl: returnUrl ?? `${window.location.origin}/billing`,
            environment: env,
          },
        });
        const url = (data as any)?.url;
        if (error || !url) {
          throw new Error(error?.message || 'Failed to open billing portal');
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (e: any) {
        toast({
          title: 'Unable to open billing portal',
          description: e?.message ?? 'Please try again in a moment.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    },
    [toast],
  );

  return { open, loading };
}
